"use client";

/**
 * @file 认证上下文组件
 * @description 提供全局认证状态管理功能，包括登录、登出和会话状态
 * 使用"use client"指令标记为客户端组件，因为需要访问浏览器API和处理用户交互
 */

import { createContext, useContext } from "react";
import { useSession } from "next-auth/react";
import { signIn, signOut } from "../auth";
import type { Session } from "next-auth";

/**
 * 认证上下文类型接口
 * @description 定义认证上下文中暴露的状态和方法
 */
type AuthContextType = {
  session: Session | null;                     // 用户会话数据
  status: "loading" | "authenticated" | "unauthenticated"; // 认证状态
  signIn: (provider?: string) => Promise<void>; // 登录方法
  signOut: () => Promise<void>;                // 登出方法
};

/**
 * 创建认证上下文
 * @description 初始值为undefined，需要在使用前确保存在提供者
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 认证提供者组件
 * @description 管理认证状态并将其提供给应用的其他部分
 * @param {object} props - 组件属性
 * @param {React.ReactNode} props.children - 子组件
 * @returns {JSX.Element} 认证上下文提供者
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // 使用NextAuth的useSession钩子获取会话数据和状态
  const { data: session, status } = useSession();

  /**
   * 封装登录函数
   * @description 调用NextAuth的signIn方法，默认使用谷歌提供者
   * @param {string} provider - 认证提供者，默认为"google"
   */
  const handleSignIn = async (provider?: string) => {
    await signIn(provider || "google", { callbackUrl: "/" }); // 登录成功后重定向到首页
  };

  /**
   * 封装登出函数
   * @description 调用NextAuth的signOut方法
   */
  const handleSignOut = async () => {
    await signOut({ redirectTo: "/" }); // 登出后重定向到首页
  };

  /**
   * 渲染认证上下文提供者
   * @description 将认证状态和方法提供给子组件
   */
  return (
    <AuthContext.Provider
      value={{
        session,        // 用户会话数据
        status,         // 认证状态
        signIn: handleSignIn,  // 登录方法
        signOut: handleSignOut, // 登出方法
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * 认证钩子
 * @description 提供一个简单的方式来访问和操作认证上下文
 * @returns {AuthContextType} 包含当前认证状态和认证方法
 * @throws {Error} 如果在AuthProvider外部使用，则抛出错误
 */
export function useAuth() {
  // 从上下文中获取认证状态
  const context = useContext(AuthContext);

  // 验证上下文是否存在
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
